import { useMemo } from "react";
import { useRole, UserRole } from "@/data/RoleContext";
import {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  getPermissionsByModule,
  getAllowedModules,
  PermissionKey,
  PermissionModule,
} from "@/data/permissions";
import {
  getModulePermissions,
  canView,
  canInteract,
  canEdit,
  canApprove,
  getRestrictionReason,
  isResultLocked,
  canEditPricing,
  canModifyCommissions,
  type ModuleKey,
  type ModulePermissions,
} from "@/data/rolePermissionMatrix";

/**
 * Hook for checking both legacy granular permissions and the newer
 * module-level permission matrix.
 */
export function usePermissions() {
  const { role } = useRole();

  return useMemo(() => ({
    role,

    hasPermission: (permission: PermissionKey) => hasPermission(role, permission),
    hasAnyPermission: (permissions: PermissionKey[]) => hasAnyPermission(role, permissions),
    hasAllPermissions: (permissions: PermissionKey[]) => hasAllPermissions(role, permissions),
    getPermissionsByModule: () => getPermissionsByModule(role),
    getAllowedModules: () => getAllowedModules(role),

    /** Get full permission object for a module */
    getPermissions: (module: ModuleKey): ModulePermissions =>
      getModulePermissions(role, module),

    /** Can the user see this module? */
    canView: (module: ModuleKey | PermissionModule) =>
      canView(role, module as ModuleKey),

    /** Can the user interact (click, filter, navigate) with this module? */
    canInteract: (module: ModuleKey) => canInteract(role, module),

    /** Can the user edit data in this module? */
    canEdit: (module: ModuleKey | PermissionModule) => canEdit(role, module as ModuleKey),

    canCreate: (module: PermissionModule) => hasPermission(role, `${module}_create` as PermissionKey),
    canDelete: (module: PermissionModule) => hasPermission(role, `${module}_delete` as PermissionKey),

    /** Can the user approve/override in this module? */
    canApprove: (module: ModuleKey | PermissionModule) => canApprove(role, module as ModuleKey),

    canExport: (module: PermissionModule) => hasPermission(role, `${module}_export` as PermissionKey),

    /** Get tooltip text explaining why something is restricted */
    getRestriction: (module: ModuleKey) => getRestrictionReason(role, module),

    /** Are appointment results locked? (always true) */
    isResultLocked: isResultLocked(role),

    /** Can this role edit pricing? */
    canEditPricing: canEditPricing(role),

    /** Can this role modify commissions? (always false) */
    canModifyCommissions: canModifyCommissions(role),
  }), [role]);
}

export function usePermissionsForRole(targetRole: UserRole) {
  return {
    hasPermission: (permission: PermissionKey) => hasPermission(targetRole, permission),
    hasAnyPermission: (permissions: PermissionKey[]) => hasAnyPermission(targetRole, permissions),
    hasAllPermissions: (permissions: PermissionKey[]) => hasAllPermissions(targetRole, permissions),
    getPermissionsByModule: () => getPermissionsByModule(targetRole),
    getAllowedModules: () => getAllowedModules(targetRole),
    role: targetRole,
  };
}
